// TODO external submit handler, for just changing info or purchasing in other view
import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import { connect, useDispatch } from 'react-redux';
import { Col, Row } from 'react-flexbox-grid';
import {
  FormAlert,
  NewInputText,
  handleFinalFormError,
} from '@saucelabs/sl-component-form';
import { constants as userConstants } from '@saucelabs/sl-module-user';
import { Button } from '@saucelabs/ds-button';
import { Loader } from '@saucelabs/sl-component-loader';
import validate from '@saucelabs/sl-util-validate';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Form, Field, FormSpy } from 'react-final-form';
import { OnChange } from 'react-final-form-listeners';
import { actions as notifications } from '@saucelabs/sl-container-notifications';
import { constants as notificationConstants } from '@saucelabs/sl-module-notifications';
import { Typography } from '@saucelabs/ds-typography';

import {
  cardCodeToWording,
  formatPaymentFormValues,
  getPostalCodeValidationRegexFor,
  showSuccessMessage,
} from '../utils';
import InfoIcon from '../images/question_mark.svg';
import BaseStyles from '../components/Layout/base';
import Styles from './PaymentForm.styles';
import {
  COUNTRIES_ZIP_MANDATORY,
  INVALID_ADDRESS_ERROR_TYPE,
  THREEDS_AUTH_ERROR_TYPE,
  THREEDS_FAILURE_MESSAGE,
  TRANSACTION_ERROR_TYPE,
  NOTIFICATION_ID_ERROR_FORM_DATA,
} from '../constants';
import {
  getAvailableCountries,
  getCountries,
  getCountryByCode,
  getStateByCountryCode,
} from '../utils/locations';
import RecurlyHelper from './RecurlyHelper';
import {
  fetchRecurlyTokenAction,
  paymentComponentFetchAction,
  paymentComponentReadyAction,
  paymentInfoSavedAction,
  paymentMethodChangedAction,
  retrieveAccountAction,
  updateAccountAction,
  updateBillingInfoAction,
} from './../ducks/billing-info/actions';
import { setOrganizationCountry as setOrganizationCountryOperation } from './../ducks/billing-info/operations';
import { paymentFormSelector } from './../ducks/billing-info/selectors';
import CreditCardComponent from './CreditCardComponent';
import { FormSelect } from './FormSelect';
import { handle3DSecure } from '../utils/threeDSecure';
import { trackEvent, EVENTS } from './../tracking';

const constraints = {
  firstName: {
    presence: { allowEmpty: false, message: 'Please enter cardholder name' },
  },
  lastName: {
    presence: { allowEmpty: false, message: 'Please enter cardholder name' },
  },
  email: {
    email: true,
    presence: { allowEmpty: false, message: 'Please enter your email' },
  },
  address1: {
    presence: { allowEmpty: false, message: 'Please enter your street address' },
  },
  country: {
    presence: { allowEmpty: false, message: 'Please enter your country' },
  },
  city: {
    presence: { allowEmpty: false, message: 'Please enter your city' },
  },
  state: {
    presence: { allowEmpty: false, message: 'Please enter your state' },
  },
  postalCode: (value, attributes) => {
    // The postal code is mandatory only for certain countries
    if (COUNTRIES_ZIP_MANDATORY.includes(attributes.country)) {
      return {
        presence: { allowEmpty: false, message: 'Please enter your zip code' },
        format: (val, attrs) => {
          const { country } = attrs;
          const postalCodeRegex = getPostalCodeValidationRegexFor(country);
          return postalCodeRegex?.validationRegex;
        },
      };
    }
    return null;
  },
};

const handleUpdateInformationClick = (cardStatus) => {
  trackEvent(EVENTS.BILLING_INFO_CONFIRM_UPDATE);
  if (cardStatus.valid) {
    trackEvent(EVENTS.BILLING_INFO_CARD_CHECK);
  }
};

// This function has been consciously moved outside of the component, so that you don't get
// tempted to use values available before it, as they may come from a previous render.
// You need to pass all the values explicitly from the place it is called.
const debouncedOnFormChange = debounce((
  form,
  valid,
  isPurchase,
  creditCardStatus,
) => {
  // We only want to trigger this once, as soon as credit card information is first populated,
  // or whenever credit card info is being changed. The reason for that is that the form values
  // are included in /payment payload - except for the credit card.
  // We also need to react for country change, because a different tax rate needs to be applied.
  if (valid && isPurchase) {
    // if setReadOnly false and valid true means credit card has been changed
    if (creditCardStatus.valid) {
      trackEvent(EVENTS.CHECKOUT_CARD_CHECK);
    }
    form.submit();
  }
}, 3000);

const debouncedOnFieldChange = debounce((fieldName, isPurchase) => (
  trackEvent(
    isPurchase ? EVENTS.CHECKOUT_UPDATE_FORM_FIELD : EVENTS.BILLING_INFO_UPDATE_FORM_FIELD,
    { fieldName })
), 1000);

const useCreditCardState = () => {
  // all hooks related to credit card state tracking and interactions
  const [isValid, setCardStatus] = React.useState(false);
  const [isDirty, setIsDirty] = React.useState(false);
  const [isReadOnly, setReadOnly] = React.useState(false);
  const setIsCardValid = React.useCallback((valid, dirty = true) => {
    setCardStatus(valid);
    if (dirty) {
      setIsDirty(true);
    }
  }, [isValid],
  );

  const setReadOnlyCard = React.useCallback((value) => {
    setCardStatus(false);
    setReadOnly(value);
  }, [isValid]);

  const onChange = React.useRef();
  const registerCallback = (callbackFn) => {
    onChange.current = callbackFn;
  };
  React.useEffect(() => {
    onChange.current?.(isValid || isReadOnly);
  },
  [isValid, isReadOnly],
  );
  const setPristine = () => setIsDirty(false);

  const cardState = {
    dirty: isDirty,
    valid: isValid,
    readOnly: isReadOnly,
  };

  const functions = {
    setReadOnlyCard,
    setIsCardValid,
    registerCallback,
    setPristine,
  };
  return [cardState, functions];
};

const PaymentForm = ({
  fetchAccount,
  fetchToken,
  onFormStateChange,
  initialValues,
  isLoading,
  isPurchase,
  onPaymentInfoSet,
  paymentInfo,
  paymentInfoSaved,
  paymentInputChange,
  paymentMethod,
  paymentMethodCurrent,
  recurlyComponentFetch,
  recurlyComponentReady,
  recurlyInstance,
  setOrganizationCountry,
  updateAccount,
  updateBillingInfo,
}) => {
  const dispatch = useDispatch();
  const formRef = useRef();
  const [canShowCreditCardError, setCanShowCreditCardError] = useState(false);
  const [cardStatus, cardFunctions] = useCreditCardState();
  const [countryOptions, setCountryOptions] = useState(getCountries());

  const recurlyOnChange = (recurlyArg) => {
    const { fields: { card } } = recurlyArg;
    // Recurly sends change events with empty card data, which causes unwanted card reset that
    // we're trying to prevent here
    const { brand, empty, number, valid, lastFour, cvv, expiry } = card;
    if (!empty || !cvv.empty || !expiry.empty || !number.empty) {
      paymentInputChange(brand, lastFour, valid);
      const isValid = !number.empty && !cvv.empty && !expiry.empty && valid;
      cardFunctions.setIsCardValid(isValid, !empty);
    }
  };
  const afterRecurlyLoad = React.useCallback(() => {
    recurly.on('ready', recurlyComponentReady);
    recurly.on('change', recurlyOnChange, []);
  });
  useEffect(() => {
    if (!paymentMethod) return;
    // If there's a saved card
    // I must check if is the same that the user is using on this transaction
    if (
      !paymentMethodCurrent ||
      (paymentMethod.lastFour === paymentMethodCurrent.lastFour &&
      paymentMethod.cardType === paymentMethodCurrent.cardType)
    ) {
      cardFunctions.setReadOnlyCard(true);
      onPaymentInfoSet?.(); // saved payment method implies user that already has payment form filled
    }
  }, [paymentMethod?.lastFour, paymentMethod?.cardType]);

  useEffect(() => {
    recurlyComponentFetch();
    fetchAccount()
      .then((response) => {
        if (response?.data !== null) {
          setCountryOptions(getAvailableCountries(response?.data?.billing_info?.address?.country));
          if ((!isPurchase || !paymentInfo) && response?.data?.billing_info?.last_four) {
            cardFunctions.setReadOnlyCard(true);
          }
        }
      });

    return () => {
      if (recurly) {
        recurly.off('ready', recurlyComponentReady);
        recurly.off('change', paymentInputChange);

        // HACK: manually remove listeners, else recurly will dup them when reconfigured
        recurly.off('hostedField:submit');
        recurly.off('hostedFields:state:change');
      }
    };
  }, [recurlyInstance]);

  const paymentVerificationElementRef = React.createRef();

  let cleanup3DSecureListeners;
  useEffect(() => () => {
    cleanup3DSecureListeners?.();
    // we're unmounting - cancel any pending form submissions
    debouncedOnFormChange.cancel();
  }, []);

  const handleSubmitError = (responseError) => {
    const { response } = responseError;
    let { message } = responseError;

    if (response?.data && response.status === 422) {
      const { type, detail } = response.data;
      message = detail || message;

      let unprocessableErrorReason = 'There was an error while processing form data.';

      if (type === INVALID_ADDRESS_ERROR_TYPE) {
        unprocessableErrorReason = 'Your address does not correspond to a known tax jurisdiction, please correct it.';
      }

      if (type === TRANSACTION_ERROR_TYPE) {
        unprocessableErrorReason = message || 'The card was declined. Please use a different card or contact your bank.';
      }

      const notificationBody = (
        <>
          <p>
            {unprocessableErrorReason}
          </p>
          <p>
            If you have any questions or concerns, please
            {' '}
            <a
              className="underline"
              href="https://support.saucelabs.com/hc/en-us"
              rel="noopener noreferrer"
              target="_blank"
            >
              contact support
            </a>
          </p>
        </>
      );

      const payload = {
        category: notificationConstants.CATEGORY_DANGER,
        children: notificationBody,
        persist: true,
        id: NOTIFICATION_ID_ERROR_FORM_DATA,
      };

      dispatch(notifications.notify(payload));
      return;
    }

    if (responseError.code === THREEDS_AUTH_ERROR_TYPE) {
      message = THREEDS_FAILURE_MESSAGE;
    }

    handleFinalFormError({
      dispatch,
      status: responseError.status,
      errors: {},
      notifyOn40x: message,
    });
  };

  const handle3DSecureError = (error, onVerified) => {
    const { response } = error;
    const is3DSecureError = response?.data?.detail?.three_d_secure_action_token_id;
    if (!is3DSecureError) {
      return handleSubmitError(error, dispatch);
    }
    const { remove3DListeners, promise } = handle3DSecure({
      onError: (newError) => handleSubmitError(newError, dispatch),
      onVerified,
      response,
      verificationElement: paymentVerificationElementRef.current,
    });
    cleanup3DSecureListeners = remove3DListeners;
    // await 3DSecure challange so it's still a part of form submission
    // if this is not awaited, the `submitting` flag would switch back to false
    // before the 3DSecure challange is completed.
    return promise;
  };

  const saveBillingInfo = async (values, token, threeDsTokenId) => {
    const continueOn3DSecureError = (error) => (
      handle3DSecureError(error, async ({ threeDsToken = null }) => (
        saveBillingInfo(values, token, threeDsToken?.id)
      ),
      ));

    const countryName = getCountryByCode(values.billing_info.country);
    await setOrganizationCountry(countryName).catch((err) => {
      // rethrow everything except validation error
      if (err?.response?.status !== 400) throw err;
    });

    if (isPurchase) { // Checkout (part of the purchase flow)
      try {
        await paymentInfoSaved(values);
        await updateBillingInfo({ token, values, threeDsTokenId });
        onPaymentInfoSet?.({ token, values });
      } catch (error) {
        await continueOn3DSecureError(error);
      }
    } else { // Billing Information page, without purchase
      try {
        await updateAccount({ token, values, threeDsTokenId });
        showSuccessMessage('Payment Info Updated', dispatch); // TODO: move this to parent's onPaymentInfoSet handler
        cardFunctions.setReadOnlyCard(true);
        setCanShowCreditCardError(false);
        onPaymentInfoSet?.({ token, values });
      } catch (error) {
        await continueOn3DSecureError(error);
      }
    }
  };

  const submit = async (values) => {
    const data = formatPaymentFormValues(values);
    setCanShowCreditCardError(true);

    if (!cardStatus.readOnly) {
      if (cardStatus.valid) {
        try {
          const response = await fetchToken(formRef.current);
          // token fetched successfuly. Finish form submission
          await saveBillingInfo(data, response.id);
          return null;
        } catch (err) {
          handleSubmitError(err, dispatch);
        }
      }
      return null;
    }
    // When using preexistent credit card, we send a blank token
    paymentInputChange(paymentMethod.cardType, paymentMethod.lastFour, true);
    return saveBillingInfo(data, '');
  };

  return (
    <Form
      initialValues={initialValues}
      keepDirtyOnReinitialize={isPurchase}
      onSubmit={submit}
      render={({ form, handleSubmit }) => {
        const formState = form.getState();
        const { error, pristine, submitError, submitting, values } = formState;
        const actuallyPristine = pristine && cardStatus.readOnly && !isPurchase;

        const onChange = (attrs, card) => {
          onFormStateChange?.(attrs);
          const creditCardValid = card.valid || card.readOnly;
          const isDirty = attrs.dirty || card.dirty;
          if (attrs.valid && creditCardValid && !attrs.submitting && isDirty) {
            debouncedOnFormChange(
              form,
              attrs.valid,
              isPurchase,
              cardStatus,
            );
          }
        };

        // we need to inform somehow that the credit card value changed, but
        // this callback is available only from the render function, so we store it as ref.
        cardFunctions.registerCallback(() => onChange(form.getState(), cardStatus));

        return (
          <form
            ref={formRef}
            onSubmit={handleSubmit}
          >
            {isPurchase && (
              <>
                {/* form's onChange listener for autosubmit feature */}
                <FormSpy
                  onChange={() => onChange(form.getState(), cardStatus)}
                  subscription={{
                    valid: true,
                    dirtyFields: true,
                    dirtyFieldsSinceLastSubmit: true,
                  }}
                />
                {/* set credit card to pristing after the form was submitted */}
                <FormSpy
                  onChange={(attrs) => (
                    attrs.submitSucceeded &&
                    !attrs.submitting &&
                    !attrs.dirtySinceLastSubmit &&
                    cardFunctions.setPristine()
                  )}
                  subscription={{
                    submitSucceeded: true,
                    submitting: true,
                    dirtySinceLastSubmit: true,
                  }}
                />
              </>
            )}
            <div>
              <RecurlyHelper
                afterLoad={afterRecurlyLoad}
                instance={recurlyInstance}
              />
              {(
                [
                  'address1',
                  'address2',
                  'city',
                  'company',
                  'country',
                  'email',
                  'first_name',
                  'last_name',
                  'postalCode',
                  'state',
                  'vatNumber',
                ].map((fieldName) => (
                  <OnChange
                    key={fieldName}
                    name={fieldName}
                  >
                    {(value, previous) => {
                      if ((value || '') !== (previous || '')) {
                        debouncedOnFieldChange(fieldName, isPurchase);
                      }
                    }}
                  </OnChange>
                ))
              )}
              <FormAlert type="danger">{error || submitError}</FormAlert>
              <Row middle="xs">
                <Col
                  className={Styles['subtitle-row']}
                  xs={12}
                >
                  <Typography
                    className="my-0"
                    disableSpacing
                    variant="heading-3"
                  >
                    Company &amp; Contact Information
                  </Typography>
                </Col>
              </Row>
              <Row className="mt-1">
                <Col
                  md={6}
                  xs={12}
                >
                  <Field
                    className={Styles['form-field']}
                    component={NewInputText}
                    data-private
                    data-recurly="company"
                    label="Company Name"
                    name="company"
                    placeholder="Company Name"
                    showErrorsWithoutSubmit
                    type="text"
                  />
                </Col>
                <Col
                  md={6}
                  xs={12}
                >
                  <Field
                    className={Styles['form-field']}
                    component={NewInputText}
                    label="Billing Email Address"
                    name="email"
                    placeholder="Email Address"
                    showErrorsWithoutSubmit
                    type="text"
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <Field
                    className={Styles['form-field']}
                    component={NewInputText}
                    data-private
                    data-recurly="address1"
                    label="Street Address"
                    name="address1"
                    placeholder="Street and number"
                    showErrorsWithoutSubmit
                    type="text"
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <Field
                    className={Styles['form-field']}
                    component={NewInputText}
                    data-private
                    data-recurly="address2"
                    name="address2"
                    placeholder="Extra address line"
                    showErrorsWithoutSubmit
                    type="text"
                  />
                </Col>
              </Row>
              <Row>
                <Col
                  className={Styles['select-container']}
                  md={6}
                  xs={12}
                >
                  <label>
                    Country
                  </label>
                  <OverlayTrigger
                    delayHide={300}
                    overlay={(
                      <Tooltip id="eea-tooltip">
                        This selection determines where your billing data is stored
                        - if you select a country within the European Economic Area (EEA),
                        your data will be stored within the EU. Once selected, you may only
                        change your country within the same region e.g. if you select a country in
                        EEA, you may only change to another country within the EEA.
                        The same applies to countries outside of the EEA.
                      </Tooltip>
                    )}
                    placement="top"
                  >
                    <InfoIcon className={Styles['info-icon']} />
                  </OverlayTrigger>
                  <Field
                    className={Styles['form-field']}
                    component={FormSelect}
                    data-private
                    data-recurly="country"
                    name="country"
                    onChange={() => form.change('state', null)}
                    options={countryOptions}
                    placeholder="Select country"
                    showErrorsWithoutSubmit
                  />
                </Col>
                <Col
                  md={6}
                  xs={12}
                >
                  <label>
                    VAT ID
                    {' '}
                    <span className={Styles['vat-label-secondary']}>(if applicable)</span>
                  </label>

                  <OverlayTrigger
                    delayHide={300}
                    overlay={(
                      <Tooltip id="vat-tooltip">
                        Enter VAT ID to avoid being charged for Value Added Tax
                        with this purchase.
                      </Tooltip>
                    )}
                    placement="top"
                  >
                    <InfoIcon className={Styles['info-icon']} />
                  </OverlayTrigger>

                  <Field
                    className={Styles['form-field']}
                    component={NewInputText}
                    data-private
                    data-recurly="vat_number"
                    name="vatNumber"
                    showErrorsWithoutSubmit
                    type="text"
                  />
                </Col>
              </Row>
              <Row>
                <Col
                  md={6}
                  xs={12}
                >
                  <Field
                    className={Styles['form-field']}
                    component={NewInputText}
                    data-private
                    data-recurly="city"
                    label="City"
                    name="city"
                    showErrorsWithoutSubmit
                    type="text"
                  />
                </Col>
                <Col
                  className={Styles['select-container']}
                  lg={3}
                  md={3}
                  xs={12}
                >
                  <Field
                    className={Styles['form-field']}
                    component={FormSelect}
                    data-private
                    data-recurly="state"
                    disabled={!values.country}
                    label="State / Province"
                    name="state"
                    options={getStateByCountryCode(values.country)}
                    placeholder={values.country ? 'Select State / Province' : 'Select country first'}
                    showErrorsWithoutSubmit
                  />
                </Col>
                <Col
                  lg={3}
                  md={3}
                  xs={12}
                >
                  <Field
                    className={Styles['form-field']}
                    component={NewInputText}
                    data-private
                    data-recurly="postal_code"
                    label="Zip"
                    name="postalCode"
                    showErrorsWithoutSubmit
                    type="text"
                  />
                </Col>
              </Row>
              <Row middle="xs">
                <Col
                  className={Styles['subtitle-row']}
                  xs={12}
                >
                  <Typography
                    className="my-0"
                    disableSpacing
                    variant="heading-3"
                  >
                    Payment Details
                  </Typography>
                </Col>
              </Row>
              <Row>
                <Col
                  xs={12}
                >

                  <CreditCardComponent
                    canShowCreditCardError={canShowCreditCardError}
                    handleReplaceCard={() => (isPurchase ?
                      trackEvent(EVENTS.CHECKOUT_REPLACE_CARD) :
                      trackEvent(EVENTS.BILLING_INFO_REPLACE_CARD)
                    )}
                    isCardInputCompleted={cardStatus.valid}
                    paymentMethod={paymentMethod}
                    paymentMethodCurrent={paymentMethodCurrent}
                    readOnlyCard={cardStatus.readOnly}
                    setReadOnlyCard={cardFunctions.setReadOnlyCard}
                  />
                </Col>
                <Col
                  md={6}
                  xs={12}
                >
                  <Field
                    className={Styles['form-field']}
                    component={NewInputText}
                    data-private
                    data-recurly="first_name"
                    label="First Name"
                    name="firstName"
                    showErrorsWithoutSubmit
                    type="text"
                  />
                </Col>
                <Col
                  md={6}
                  xs={12}
                >
                  <Field
                    className={Styles['form-field']}
                    component={NewInputText}
                    data-private
                    data-recurly="last_name"
                    label="Last Name"
                    name="lastName"
                    showErrorsWithoutSubmit
                    type="text"
                  />
                </Col>
              </Row>
              {!isPurchase && (
                <Row>
                  <Col xs={12}>
                    <Button
                      disabled={isLoading || submitting || actuallyPristine}
                      onPress={() => handleUpdateInformationClick(cardStatus)}
                      type="submit"
                    >
                      <Loader
                        className={BaseStyles['loader-disabled']}
                        fontSize={1.4}
                        show={isLoading || submitting}
                        text="Validating..."
                      >
                        UPDATE INFORMATION
                      </Loader>
                    </Button>
                  </Col>
                </Row>
              )}
            </div>
          </form>
        );
      }}
      validate={(values) => validate(values, constraints, { fullMessages: false })}
    />
  );
};

PaymentForm.propTypes = {
  fetchAccount: PropTypes.func.isRequired,
  fetchToken: PropTypes.func.isRequired,
  initialValues: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isPurchase: PropTypes.bool.isRequired,
  onFormStateChange: PropTypes.func,
  onPaymentInfoSet: PropTypes.func,
  paymentInfo: PropTypes.shape({
    company: PropTypes.string,
    country: PropTypes.string,
    email: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  }),
  paymentInfoCurrent: PropTypes.shape({
    company: PropTypes.string,
    email: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  }),
  paymentInfoSaved: PropTypes.func.isRequired,
  paymentInputChange: PropTypes.func.isRequired,
  paymentMethod: PropTypes.shape({
    cardType: PropTypes.string,
    lastFour: PropTypes.string,
  }),
  paymentMethodCurrent: PropTypes.shape({
    cardType: PropTypes.string,
    isValid: PropTypes.bool,
    lastFour: PropTypes.string,
  }),
  recurlyComponentFetch: PropTypes.func.isRequired,
  recurlyComponentReady: PropTypes.func.isRequired,
  recurlyInstance: PropTypes.oneOf([
    userConstants.RECURLY_INSTANCES.EU,
    userConstants.RECURLY_INSTANCES.ROW,
  ]).isRequired,
  setOrganizationCountry: PropTypes.func.isRequired,
  updateAccount: PropTypes.func.isRequired,
  updateBillingInfo: PropTypes.func.isRequired,
};

PaymentForm.defaultProps = {
  onFormStateChange: null,
  onPaymentInfoSet: null,
  paymentInfo: null,
  paymentInfoCurrent: null,
  paymentMethod: null,
  paymentMethodCurrent: null,
};

const mapStateToProps = paymentFormSelector;

const mapDispatchToProps = (dispatch) => ({
  fetchAccount: () => dispatch(retrieveAccountAction()),
  fetchToken: (form) => dispatch(fetchRecurlyTokenAction(form)),
  paymentInfoSaved: (values) => dispatch(paymentInfoSavedAction(values)),
  recurlyComponentReady: () => dispatch(paymentComponentReadyAction()),
  paymentInputChange: (
    brand, lastFour, isValid,
  ) => dispatch(
    paymentMethodChangedAction(cardCodeToWording(brand), lastFour, isValid),
  ),
  recurlyComponentFetch: () => dispatch(paymentComponentFetchAction()),
  updateAccount: (params) => dispatch(updateAccountAction(params)),
  updateBillingInfo: (params) => dispatch(updateBillingInfoAction(params)),
  setOrganizationCountry: async (country) => dispatch(setOrganizationCountryOperation(country)),
});

const PaymentFormWrapperContainer = connect(mapStateToProps, mapDispatchToProps)(
  PaymentForm,
);

export default PaymentFormWrapperContainer;
export { PaymentForm, mapStateToProps, mapDispatchToProps };
